// useCatalogueStatus.ts
import * as signalR from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { API_URL } from "../_config/config.const";
import { accountService } from "../_services/accounts.service";

const API_NAME = "catalogueUser";

/** userId -> last seen UTC timestamp in milliseconds */
export type HeartbeatsMap = Record<string, number>;

/** userId -> whether the user's auction tab is visible/focused */
export type TabVisibleMap = Record<string, boolean>;

function parseHeartbeatTimestamp(value: string | Date | number): number {
    if (value == null) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") return new Date(value).getTime();
    if (value instanceof Date) return value.getTime();
    return 0;
}

function sameIdList(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((id, i) => id === sortedB[i]);
}

function mergeHeartbeats(prev: HeartbeatsMap, incoming: HeartbeatsMap): HeartbeatsMap {
    let changed = false;
    const next = { ...prev };
    Object.keys(incoming).forEach((userId) => {
        if (next[userId] !== incoming[userId]) {
            next[userId] = incoming[userId];
            changed = true;
        }
    });
    return changed ? next : prev;
}

export function useAuctionUserAdmin() {

    const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
    const [heartbeats, setHeartbeats] = useState<HeartbeatsMap>({});
    const [tabVisibleByUserId, setTabVisibleByUserId] = useState<TabVisibleMap>({});

    const [connected, setConnected] = useState(false);
    const connRef = useRef<signalR.HubConnection | null>(null);
    const [currentUserId, setCurrentUserId] = useState(accountService.currentUserValue?.id);
    const heartbeatRef = useRef<number | null>(null);

    useEffect(() => {
        const currentUser = accountService.currentUserValue;

        const conn = new signalR.HubConnectionBuilder()
            .withUrl(`${API_URL}/hubs/${API_NAME}`, {
                accessTokenFactory: () => currentUser.token
            })
            .withAutomaticReconnect([0, 1000, 2000, 5000, 10000])
            .build();

        conn.serverTimeoutInMilliseconds = 180_000;
        conn.keepAliveIntervalInMilliseconds = 5_000;

        heartbeatRef.current = window.setInterval(() => {
            if (conn.state === signalR.HubConnectionState.Connected) {
                conn.invoke("AdminCheckHeartbeats").catch(() => { });
            }
        }, 3000);

        connRef.current = conn;
        conn.on('UpdateUserList', (ids: string[]) => {
            const next = Array.isArray(ids) ? ids : [];
            setOnlineUsers((prev) => sameIdList(prev, next) ? prev : next);
        });
        conn.on('UserOffline', (userId: string) => {
            if (userId) {
                setOnlineUsers((prev) => prev.filter((id) => id !== userId));
                setHeartbeats((prev) => {
                    const next = { ...prev };
                    delete next[userId];
                    return next;
                });
                setTabVisibleByUserId((prev) => {
                    const next = { ...prev };
                    delete next[userId];
                    return next;
                });
            }
        });
        conn.on('InitialHeartbeats', (snapshot: Record<string, string | number> | null) => {
            const incoming: HeartbeatsMap = {};
            if (snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)) {
                Object.keys(snapshot).forEach((userId) => {
                    const val = snapshot[userId];
                    const ms = parseHeartbeatTimestamp(val as string | number);
                    if (ms > 0) incoming[userId] = ms;
                });
            }
            setHeartbeats((prev) => mergeHeartbeats(prev, incoming));
        });
        conn.on('InitialTabVisible', (snapshot: Record<string, boolean> | null) => {
            if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return;
            setTabVisibleByUserId((prev) => {
                let changed = false;
                const next = { ...prev };
                Object.keys(snapshot).forEach((userId) => {
                    if (next[userId] !== snapshot[userId]) {
                        next[userId] = snapshot[userId];
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        });
        conn.on('Heartbeat', (userId: string, timestamp: string | Date | number, isTabVisible?: boolean) => {
            const ms = parseHeartbeatTimestamp(timestamp);
            if (ms > 0) {
                setHeartbeats((prev) => prev[userId] === ms ? prev : { ...prev, [userId]: ms });
            }
            if (typeof isTabVisible === 'boolean') {
                setTabVisibleByUserId((prev) => prev[userId] === isTabVisible ? prev : { ...prev, [userId]: isTabVisible });
            }
        });

        (async () => {
            try {
                await conn.start();
                setConnected(true);

                conn.invoke('AdminUserConnected');

            } catch (err) {
                console.error("SignalR connect failed", err);
            }
        })();

        return () => {
            conn.stop();
            setConnected(false);

            if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
        };

    }, [currentUserId]);

    return { onlineUsers, connected, heartbeats, tabVisibleByUserId };
}
